<script setup lang="ts">
interface SliderItem {
    url?: string | null
    newTab?: boolean
    ariaLabel?: string | null
    position?: number | null
    media?: {
        url?: string | null
        alt?: string | null
        title?: string | null
    }
}

interface ImageSliderConfig {
    navigationArrows?: { value?: string | null }
    navigationDots?: { value?: string | null }
    autoSlide?: { value?: boolean }
    slidesPerView?: { value?: number }
    slidesPerViewMobile?: { value?: number }
    minHeight?: { value?: string }
    sliderItems?: { value?: Array<{ mediaId?: string; position?: number | null }> }
}

const props = defineProps<{
    content: {
        id: string
        config?: ImageSliderConfig
        data?: {
            sliderItems?: SliderItem[]
        }
    }
}>()

const config = computed(() => (props.content?.config ?? {}) as ImageSliderConfig)

const sliderItems = computed(() => {
    const dataItems = props.content?.data?.sliderItems ?? []
    const configItems = config.value.sliderItems?.value ?? []

    return [...dataItems]
        .map((item, index) => ({ ...item, position: configItems[index]?.position ?? null }))
        .sort((a, b) => (a.position ?? Infinity) - (b.position ?? Infinity))
})
const navigationArrows = computed(() => {
    const val = config.value.navigationArrows?.value
    if (!val || val === 'none' || val === '') return false
    return val
})

const navigationDots = computed(() => {
    const val = config.value.navigationDots?.value
    return !(!val || val === 'none' || val === '')
})

const autoSlide = computed(() => config.value.autoSlide?.value ?? false)

const slidesPerView = computed(() => Number(config.value.slidesPerView?.value) || 1)
const slidesPerViewMobile = computed(() => Number(config.value.slidesPerViewMobile?.value) || 1)
const minHeight = computed(() => config.value.minHeight?.value || '300px')
</script>

<template>
    <div class="cms-element-image-slider md:px-6">
        <ComponentCarousel
            :slides="(sliderItems as any[])"
            :label="$t('carousel.label')"
            :breakpoints="{
                default: { slidesPerView: slidesPerViewMobile },
                md: { slidesPerView: slidesPerView },
                lg: { slidesPerView: slidesPerView },
            }"
            :gap="10"
            :enable-arrows="!!navigationArrows"
            :enable-dots="navigationDots"
            :enable-autoplay="autoSlide"
            enable-infinite-scroll
            carousel-width="100%"
            left-arrow-class="btn btn-square bg-transparent z-40 border-none hidden md:flex absolute top-1/2 -translate-y-1/2 -left-6"
            right-arrow-class="btn btn-square bg-transparent z-40 border-none hidden md:flex absolute top-1/2 -translate-y-1/2 -right-6"
        >
            <template #default="{ index }">
                <component
                    :is="sliderItems[index]?.url ? 'a' : 'div'"
                    v-if="sliderItems[index]?.media?.url"
                    :href="sliderItems[index]?.url || undefined"
                    :target="sliderItems[index]?.newTab ? '_blank' : undefined"
                    :rel="sliderItems[index]?.newTab ? 'noopener noreferrer' : undefined"
                    :aria-label="sliderItems[index]?.ariaLabel || undefined"
                    class="block w-full"
                    :style="{ minHeight }"
                >
                    <FoundationImage
                        :src="sliderItems[index]?.media?.url || ''"
                        :alt="sliderItems[index]?.media?.alt || ''"
                        class="w-full h-full object-contain"
                        :style="{ minHeight }"
                    />
                </component>
            </template>

            <template #left-arrow="{ previous }">
                <FoundationButton
                    class="btn-square"
                    :aria-label="$t('carousel.previous')"
                    @click="previous()"
                >
                    <FoundationIcon name="chevron-left" size="xl" />
                </FoundationButton>
            </template>

            <template #right-arrow="{ next }">
                <FoundationButton
                    class="btn-square"
                    :aria-label="$t('carousel.next')"
                    @click="next()"
                >
                    <FoundationIcon name="chevron-right" size="xl" />
                </FoundationButton>
            </template>
        </ComponentCarousel>
    </div>
</template>
